/**
 * RecoveryManager — Disaster Recovery function group, MVP slice.
 *
 * The fallback layer. Functions here are designed to keep working when
 * the network is gone. Briar is the architectural reference: assume the
 * internet is down, design for that.
 *
 * Scope (function map MVP):
 *   - enqueueOutbound / drainOutboundQueue — when send fails, queue and retry
 *   - enqueueInbound / processInboundQueue — for envelopes received via
 *     side-channels (file import, QR scan) or for offline-batch processing
 *   - exportQueueToBundle — serialize outbound queue to a portable bundle
 *   - importQueueFromBundle — receive a bundle and enqueue its contents
 *   - createRecoverySnapshot / restoreFromSnapshot / listSnapshots
 *
 * Not in scope (later):
 *   - QR codec specifics (frame splitting, error correction). Bundle
 *     bytes are produced; QR encoding is an application-level concern.
 *   - BLE mesh transport. Whole separate effort.
 *   - Optional passphrase encryption of the export bundle.
 *
 * Storage backends:
 *   - Queues (outbound_queue, inbound_queue): Dexie tables
 *   - Snapshots: encrypted blobs in OPFS (production) or in-memory (tests).
 *     The chosen storage backend is injected via the `SnapshotStorage`
 *     interface so the same RecoveryManager works in both environments.
 *
 * Failure mode philosophy: every function here works offline by definition.
 * That's the job. If a function in this group hangs on network reachability,
 * it's a bug.
 */

import { v4 as uuid } from 'uuid';

import type { AppContext } from '../app';
import type { SignedEnvelope } from '../envelope/Envelope';
import type {
  AuditLogRecord,
  InboundQueueRecord,
  OutboundQueueRecord,
  RecoverySnapshotRecord,
  SnapshotScope,
  SnapshotTrigger,
} from '../storage/schema';

// ─────────────────────────────────────────────────────────────────────────────
// SNAPSHOT STORAGE INTERFACE
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Pluggable storage backend for snapshot blob bytes. Production uses the
 * OPFSSnapshotStorage implementation; tests use InMemorySnapshotStorage.
 *
 * The metadata row (RecoverySnapshotRecord) is always in IndexedDB; only
 * the encrypted blob is offloaded.
 */
export interface SnapshotStorage {
  /** Write bytes to the given logical path. Returns the resolved path
   * (may differ from input — e.g., OPFS may normalize). */
  write(path: string, bytes: Uint8Array): Promise<string>;
  /** Read bytes by logical path. */
  read(path: string): Promise<Uint8Array>;
  /** Delete bytes by logical path. */
  delete(path: string): Promise<void>;
  /** List all stored paths. */
  list(): Promise<string[]>;
}

/**
 * In-memory snapshot storage. Suitable for tests and ephemeral
 * environments where snapshots don't need to persist across page reloads.
 */
export class InMemorySnapshotStorage implements SnapshotStorage {
  #store = new Map<string, Uint8Array>();

  async write(path: string, bytes: Uint8Array): Promise<string> {
    this.#store.set(path, bytes);
    return path;
  }
  async read(path: string): Promise<Uint8Array> {
    const v = this.#store.get(path);
    if (v === undefined) throw new Error(`InMemorySnapshotStorage: no entry at ${path}`);
    return v;
  }
  async delete(path: string): Promise<void> {
    this.#store.delete(path);
  }
  async list(): Promise<string[]> {
    return Array.from(this.#store.keys());
  }
}

/**
 * OPFS-backed snapshot storage. Browser-only — uses the Origin Private
 * File System API. Returns sensible errors when OPFS is unavailable.
 *
 * Paths are namespaced under a `surface-mesh-snapshots/` directory.
 */
export class OPFSSnapshotStorage implements SnapshotStorage {
  static readonly DIR = 'surface-mesh-snapshots';

  private async getRoot(): Promise<FileSystemDirectoryHandle> {
    const navStorage = (
      globalThis as unknown as {
        navigator?: { storage?: { getDirectory?: () => Promise<FileSystemDirectoryHandle> } };
      }
    ).navigator?.storage;
    if (navStorage?.getDirectory === undefined) {
      throw new Error('OPFSSnapshotStorage: OPFS not available in this environment');
    }
    const root = await navStorage.getDirectory();
    return root.getDirectoryHandle(OPFSSnapshotStorage.DIR, { create: true });
  }

  async write(path: string, bytes: Uint8Array): Promise<string> {
    const dir = await this.getRoot();
    const file = await dir.getFileHandle(path, { create: true });
    const writable = await (file as unknown as {
      createWritable: () => Promise<{ write: (b: Uint8Array) => Promise<void>; close: () => Promise<void> }>;
    }).createWritable();
    await writable.write(bytes);
    await writable.close();
    return path;
  }

  async read(path: string): Promise<Uint8Array> {
    const dir = await this.getRoot();
    const file = await dir.getFileHandle(path);
    const blob = await file.getFile();
    return new Uint8Array(await blob.arrayBuffer());
  }

  async delete(path: string): Promise<void> {
    const dir = await this.getRoot();
    await (dir as unknown as { removeEntry: (n: string) => Promise<void> }).removeEntry(path);
  }

  async list(): Promise<string[]> {
    const dir = await this.getRoot();
    const out: string[] = [];
    const entries = (dir as unknown as {
      entries: () => AsyncIterable<[string, unknown]>;
    }).entries();
    for await (const [name] of entries) {
      out.push(name);
    }
    return out;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC API
// ─────────────────────────────────────────────────────────────────────────────

export interface EnqueueOutboundOptions {
  streamId: string;
  envelope: SignedEnvelope;
  context: string;
}

export interface DrainOutboundOptions {
  /** Maximum number of envelopes to attempt in this drain call. Default 100. */
  maxBatch?: number;
}

export interface DrainOutboundResult {
  attempted: number;
  succeeded: number;
  failed: number;
  failures: Array<{ hash: string; error: string }>;
}

export interface EnqueueInboundOptions {
  streamId: string;
  envelope: SignedEnvelope;
  context: string;
  source: 'p2p' | 'file' | 'qr' | 'ble';
}

export interface ProcessInboundOptions {
  maxBatch?: number;
  /** Optional verifier callback. If provided, called per envelope; should
   * return true if the envelope is valid. Default: trust everything. */
  verify?: (env: SignedEnvelope) => Promise<boolean>;
}

export interface ProcessInboundResult {
  verified: number;
  rejected: number;
}

export interface ExportQueueResult {
  /** Serialized bundle bytes. UTF-8 JSON for v1. */
  bundleBytes: Uint8Array;
  /** Count of envelopes included in the export. */
  envelopeCount: number;
  /** When the export was created. */
  exportedAt: string;
}

export interface ImportQueueResult {
  /** Count of envelopes successfully enqueued into inbound_queue. */
  imported: number;
  /** Count that were rejected due to malformation or duplicates. */
  skipped: number;
  /** Per-envelope rejection reasons (truncated to first 10). */
  errors: string[];
}

export interface CreateSnapshotOptions {
  /** What state to include. Default 'full'. */
  scope?: SnapshotScope;
  /** Why this snapshot is being made. Default 'manual'. */
  trigger?: SnapshotTrigger;
}

export interface CreateSnapshotResult {
  snapshotId: string;
  size: number;
  createdAt: string;
}

export interface RestoreSnapshotOptions {
  snapshotId: string;
  /** If true, restore overrides existing rows. Default false: only inserts
   * missing rows. */
  overwrite?: boolean;
}

export interface RestoreSnapshotResult {
  /** Number of rows restored per table. */
  restored: Record<string, number>;
}

// ─────────────────────────────────────────────────────────────────────────────
// IMPLEMENTATION
// ─────────────────────────────────────────────────────────────────────────────

const BUNDLE_VERSION = 1 as const;

export class RecoveryManager {
  #ctx: AppContext;
  #snapshots: SnapshotStorage;

  /**
   * @param ctx       AppContext
   * @param snapshots Snapshot blob storage. Pass `new OPFSSnapshotStorage()`
   *                  in production, `new InMemorySnapshotStorage()` in tests.
   */
  constructor(ctx: AppContext, snapshots: SnapshotStorage = new InMemorySnapshotStorage()) {
    this.#ctx = ctx;
    this.#snapshots = snapshots;
  }

  // ───────────────────────────────────────────────────────────────────────
  // OUTBOUND QUEUE
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Queue an envelope for outbound publication. Used when a direct send
   * fails (network down) or when batch publishing is preferred.
   */
  async enqueueOutbound(opts: EnqueueOutboundOptions): Promise<void> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;

    const record: OutboundQueueRecord = {
      hash: opts.envelope.hash,
      context: opts.context,
      streamId: opts.streamId,
      envelopeBytes: serializeEnvelope(opts.envelope),
      queuedAt: new Date().toISOString(),
      attempts: 0,
    };

    await db.outbound_queue.put(record);
  }

  /**
   * Drain the outbound queue, attempting to publish each envelope via the
   * sync provider. Successful publishes are deleted from the queue;
   * failures stay queued with attempts incremented.
   */
  async drainOutboundQueue(opts: DrainOutboundOptions = {}): Promise<DrainOutboundResult> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;
    const maxBatch = opts.maxBatch ?? 100;

    const rows = await db.outbound_queue
      .orderBy('queuedAt')
      .limit(maxBatch)
      .toArray();

    const result: DrainOutboundResult = {
      attempted: 0,
      succeeded: 0,
      failed: 0,
      failures: [],
    };

    for (const row of rows) {
      result.attempted += 1;
      let envelope: SignedEnvelope;
      try {
        envelope = deserializeEnvelope(row.envelopeBytes);
      } catch (err) {
        // Corrupt row — drop it.
        await db.outbound_queue.delete(row.hash);
        result.failed += 1;
        result.failures.push({ hash: row.hash, error: `corrupt: ${errMsg(err)}` });
        continue;
      }
      try {
        await ctx.sync.append(row.streamId, envelope);
        await db.outbound_queue.delete(row.hash);
        result.succeeded += 1;
      } catch (err) {
        const msg = errMsg(err);
        await db.outbound_queue.update(row.hash, {
          attempts: row.attempts + 1,
          lastAttemptAt: new Date().toISOString(),
          lastError: msg,
        });
        result.failed += 1;
        result.failures.push({ hash: row.hash, error: msg });
      }
    }

    await this.audit({
      category: 'recovery',
      action: 'recovery.drained-outbound',
      context: null,
      details: {
        attempted: result.attempted,
        succeeded: result.succeeded,
        failed: result.failed,
      },
    });

    return result;
  }

  // ───────────────────────────────────────────────────────────────────────
  // INBOUND QUEUE
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Queue an envelope received from a side-channel (file/QR/BLE).
   * Status starts as 'pending'; processInboundQueue() runs verification.
   */
  async enqueueInbound(opts: EnqueueInboundOptions): Promise<void> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;

    const record: InboundQueueRecord = {
      hash: opts.envelope.hash,
      context: opts.context,
      streamId: opts.streamId,
      envelopeBytes: serializeEnvelope(opts.envelope),
      receivedAt: new Date().toISOString(),
      source: opts.source,
      status: 'pending',
    };

    await db.inbound_queue.put(record);
  }

  /**
   * Process pending inbound items: run verification, mark status.
   * Note: this does NOT apply verified envelopes to local state. Each
   * function group's own inbox (e.g., ThreadInbox) is responsible for
   * actually consuming verified envelopes from the queue. For now the
   * application-side wiring is left to the caller; future work adds an
   * `onVerified` callback or a separate Drain method.
   */
  async processInboundQueue(opts: ProcessInboundOptions = {}): Promise<ProcessInboundResult> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;
    const maxBatch = opts.maxBatch ?? 100;

    const pending = await db.inbound_queue
      .where('status')
      .equals('pending')
      .limit(maxBatch)
      .toArray();

    const result: ProcessInboundResult = { verified: 0, rejected: 0 };

    for (const row of pending) {
      let envelope: SignedEnvelope;
      try {
        envelope = deserializeEnvelope(row.envelopeBytes);
      } catch (err) {
        await db.inbound_queue.update(row.hash, {
          status: 'rejected',
          rejectionReason: `corrupt: ${errMsg(err)}`,
        });
        result.rejected += 1;
        continue;
      }

      let isValid = true;
      if (opts.verify !== undefined) {
        try {
          isValid = await opts.verify(envelope);
        } catch (err) {
          isValid = false;
          await db.inbound_queue.update(row.hash, {
            status: 'rejected',
            rejectionReason: `verifier threw: ${errMsg(err)}`,
          });
          result.rejected += 1;
          continue;
        }
      }

      if (isValid) {
        await db.inbound_queue.update(row.hash, { status: 'verified' });
        result.verified += 1;
      } else {
        await db.inbound_queue.update(row.hash, {
          status: 'rejected',
          rejectionReason: 'verifier returned false',
        });
        result.rejected += 1;
      }
    }

    await this.audit({
      category: 'recovery',
      action: 'recovery.processed-inbound',
      context: null,
      details: { verified: result.verified, rejected: result.rejected },
    });

    return result;
  }

  // ───────────────────────────────────────────────────────────────────────
  // BUNDLE EXPORT/IMPORT
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Serialize the entire outbound queue into a portable bundle. The
   * bundle is plaintext JSON; the envelopes inside remain MLS-encrypted
   * at the application layer, so message content stays safe. Metadata
   * (stream IDs, timing) is plaintext — known trade-off, documented.
   *
   * The caller writes the bytes to a file or splits into QR frames.
   * RecoveryManager does not touch the filesystem.
   */
  async exportQueueToBundle(): Promise<ExportQueueResult> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;
    const rows = await db.outbound_queue.toArray();

    const exportedAt = new Date().toISOString();
    const bundle = {
      kind: 'mesh-queue-export',
      v: BUNDLE_VERSION,
      exportedAt,
      entries: rows.map((r) => ({
        streamId: r.streamId,
        context: r.context,
        envelope: bytesToBase64(r.envelopeBytes),
      })),
    };

    const json = JSON.stringify(bundle);
    const bundleBytes = new TextEncoder().encode(json);

    await this.audit({
      category: 'recovery',
      action: 'recovery.export-bundle',
      context: null,
      details: { envelopeCount: rows.length, byteSize: bundleBytes.length },
    });

    return {
      bundleBytes,
      envelopeCount: rows.length,
      exportedAt,
    };
  }

  /**
   * Import a bundle produced by exportQueueToBundle. Each entry is
   * enqueued into inbound_queue with source='file'. Duplicates (by
   * envelope hash) are skipped.
   */
  async importQueueFromBundle(bundleBytes: Uint8Array): Promise<ImportQueueResult> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;

    let parsed: unknown;
    try {
      parsed = JSON.parse(new TextDecoder().decode(bundleBytes));
    } catch (err) {
      throw new Error(`importQueueFromBundle: malformed JSON: ${errMsg(err)}`);
    }
    if (
      typeof parsed !== 'object' || parsed === null ||
      (parsed as { kind?: unknown }).kind !== 'mesh-queue-export'
    ) {
      throw new Error('importQueueFromBundle: not a mesh-queue-export bundle');
    }
    const p = parsed as { v?: number; entries?: unknown[] };
    if (p.v !== BUNDLE_VERSION) {
      throw new Error(`importQueueFromBundle: unsupported bundle version ${p.v}`);
    }
    if (!Array.isArray(p.entries)) {
      throw new Error('importQueueFromBundle: bundle.entries must be an array');
    }

    const result: ImportQueueResult = { imported: 0, skipped: 0, errors: [] };
    const now = new Date().toISOString();

    for (const e of p.entries) {
      if (typeof e !== 'object' || e === null) {
        result.skipped += 1;
        if (result.errors.length < 10) result.errors.push('entry not an object');
        continue;
      }
      const entry = e as { streamId?: unknown; context?: unknown; envelope?: unknown };
      if (
        typeof entry.streamId !== 'string' ||
        typeof entry.context !== 'string' ||
        typeof entry.envelope !== 'string'
      ) {
        result.skipped += 1;
        if (result.errors.length < 10) result.errors.push('entry missing required fields');
        continue;
      }
      let envelope: SignedEnvelope;
      try {
        const envBytes = base64ToBytes(entry.envelope);
        envelope = deserializeEnvelope(envBytes);
      } catch (err) {
        result.skipped += 1;
        if (result.errors.length < 10) result.errors.push(`deserialize: ${errMsg(err)}`);
        continue;
      }

      const existing = await db.inbound_queue.get(envelope.hash);
      if (existing !== undefined) {
        result.skipped += 1;
        continue;
      }

      const record: InboundQueueRecord = {
        hash: envelope.hash,
        context: entry.context,
        streamId: entry.streamId,
        envelopeBytes: base64ToBytes(entry.envelope),
        receivedAt: now,
        source: 'file',
        status: 'pending',
      };
      await db.inbound_queue.put(record);
      result.imported += 1;
    }

    await this.audit({
      category: 'recovery',
      action: 'recovery.import-bundle',
      context: null,
      details: { imported: result.imported, skipped: result.skipped },
    });

    return result;
  }

  // ───────────────────────────────────────────────────────────────────────
  // SNAPSHOTS
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Create an encrypted snapshot of the current local state. The blob
   * is stored via the SnapshotStorage backend (OPFS in production); the
   * metadata row goes into recovery_snapshots.
   *
   * Scope:
   *   - 'keyring-only'      → just the keyring backup blob
   *   - 'keyring+envelopes' → keyring + envelopes table
   *   - 'full'              → keyring + envelopes + threads + contacts + ...
   *
   * Encryption uses the session key (via storage.encryptForSession).
   * Restore requires an unlocked keyring of the original identity.
   */
  async createSnapshot(opts: CreateSnapshotOptions = {}): Promise<CreateSnapshotResult> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;
    const scope = opts.scope ?? 'full';
    const trigger = opts.trigger ?? 'manual';
    const snapshotId = `snap-${Date.now()}-${uuid()}`;
    const createdAt = new Date().toISOString();
    const opfsPath = `${snapshotId}.bin`;

    // Collect snapshot contents.
    const contents = await this.collectSnapshotContents(scope);
    const json = JSON.stringify(contents);
    const plaintextBytes = new TextEncoder().encode(json);

    // Encrypt via session key. encryptForSession returns the AES-GCM
    // ciphertext+nonce+tag bundle in one Uint8Array.
    const ciphertext = await ctx.storage.encryptForSession(plaintextBytes);

    // Nonce field on the schema is for compatibility with future
    // out-of-keyring encryption modes; for now it's an empty marker.
    const nonce = new Uint8Array(0);

    // Write the blob via the storage backend.
    await this.#snapshots.write(opfsPath, ciphertext);

    const record: RecoverySnapshotRecord = {
      snapshotId,
      scope,
      createdAt,
      size: ciphertext.length,
      opfsPath,
      nonce,
      trigger,
    };
    await db.recovery_snapshots.put(record);

    await this.audit({
      category: 'recovery',
      action: 'recovery.snapshot-created',
      context: null,
      details: { snapshotId, scope, trigger, size: ciphertext.length },
    });

    return { snapshotId, size: ciphertext.length, createdAt };
  }

  /**
   * Restore a previously-created snapshot. The current session key must
   * be the same as the one used to create the snapshot (i.e., same
   * identity, unlocked).
   *
   * By default, restore is non-destructive: it only inserts missing rows.
   * Pass `overwrite: true` to make it authoritative.
   */
  async restoreFromSnapshot(opts: RestoreSnapshotOptions): Promise<RestoreSnapshotResult> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;

    const meta = await db.recovery_snapshots.get(opts.snapshotId);
    if (!meta) {
      throw new Error(`restoreFromSnapshot: unknown snapshot ${opts.snapshotId}`);
    }

    // Read encrypted bytes and decrypt.
    const ciphertext = await this.#snapshots.read(meta.opfsPath);
    let plaintextBytes: Uint8Array;
    try {
      plaintextBytes = await ctx.storage.decryptForSession(ciphertext);
    } catch (err) {
      throw new Error(`restoreFromSnapshot: decryption failed: ${errMsg(err)}`);
    }

    const json = new TextDecoder().decode(plaintextBytes);
    let contents: SnapshotContents;
    try {
      contents = JSON.parse(json) as SnapshotContents;
    } catch (err) {
      throw new Error(`restoreFromSnapshot: parse failed: ${errMsg(err)}`);
    }

    const restored: Record<string, number> = {};
    const overwrite = opts.overwrite === true;

    for (const [tableName, rows] of Object.entries(contents.tables)) {
      const table = (db as unknown as Record<string, { get?: (k: unknown) => Promise<unknown>; put: (r: unknown) => Promise<unknown>; add: (r: unknown) => Promise<unknown> }>)[tableName];
      if (table === undefined || typeof table.put !== 'function') {
        // Table not present in this DB version; skip with a log.
        // eslint-disable-next-line no-console
        console.warn(`restoreFromSnapshot: table ${tableName} not present; skipping ${rows.length} rows`);
        continue;
      }
      let count = 0;
      for (const row of rows) {
        try {
          if (overwrite) {
            await table.put(deserializeRowFields(row));
          } else {
            // Non-destructive: only put if key doesn't already exist.
            // Different tables have different primary keys; we use add()
            // which fails on conflict, and swallow the constraint error.
            try {
              await table.add(deserializeRowFields(row));
            } catch {
              continue;  // already exists; skip
            }
          }
          count += 1;
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn(`restoreFromSnapshot: row insert failed in ${tableName}: ${errMsg(err)}`);
        }
      }
      restored[tableName] = count;
    }

    await this.audit({
      category: 'recovery',
      action: 'recovery.snapshot-restored',
      context: null,
      details: {
        snapshotId: opts.snapshotId,
        scope: meta.scope,
        overwrite,
        restored,
      },
    });

    return { restored };
  }

  /** List all known snapshots, most recent first. */
  async listSnapshots(): Promise<RecoverySnapshotRecord[]> {
    const rows = await this.#ctx.storage.db.recovery_snapshots
      .orderBy('createdAt')
      .reverse()
      .toArray();
    return rows;
  }

  /**
   * Delete a single snapshot (metadata + blob).
   */
  async deleteSnapshot(snapshotId: string): Promise<void> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;
    const meta = await db.recovery_snapshots.get(snapshotId);
    if (!meta) return;  // idempotent
    try {
      await this.#snapshots.delete(meta.opfsPath);
    } catch (err) {
      // Blob may already be gone; proceed with metadata cleanup.
      // eslint-disable-next-line no-console
      console.warn(`deleteSnapshot: blob delete failed (continuing): ${errMsg(err)}`);
    }
    await db.recovery_snapshots.delete(snapshotId);

    await this.audit({
      category: 'recovery',
      action: 'recovery.snapshot-deleted',
      context: null,
      details: { snapshotId, scope: meta.scope },
    });
  }

  /**
   * Keep only the N most recent snapshots; delete older ones.
   * Returns the count deleted.
   */
  async pruneOldSnapshots(keepLastN: number): Promise<number> {
    const all = await this.listSnapshots();
    if (all.length <= keepLastN) return 0;
    const toDelete = all.slice(keepLastN);
    for (const snap of toDelete) {
      await this.deleteSnapshot(snap.snapshotId);
    }
    return toDelete.length;
  }

  // ───────────────────────────────────────────────────────────────────────
  // INTERNAL
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Gather the rows that go into a snapshot based on scope.
   *
   * Note: this layer doesn't have access to the actual keyring backup
   * blob (it's owned by the keyring SharedWorker). For 'keyring-only'
   * and 'keyring+envelopes' scopes, the caller is expected to supply
   * the keyring blob via a future opts.keyringBlob parameter. For now,
   * those scopes are effectively the same as 'full' but without the
   * keyring component (which is silently missing — a TODO(beta)).
   */
  // ───────────────────────────────────────────────────────────────────────
  // Phase 49 (v9) — cross-device account export/import
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Export a portable bundle of cross-device-relevant state, encrypted
   * under a passphrase-derived key. The recipient device imports via
   * `importBundle` with the same passphrase.
   *
   * Unlike `createSnapshot` (which uses the local session key), this
   * bundle is self-contained — the importer doesn't need to already
   * have the same identity unlocked.
   *
   * **Identity handling**: today the bundle does NOT include the
   * identity master seed itself — that lives in the keyring SharedWorker
   * and isn't exposed at this layer. The bundle is meant for state that
   * SHADOWS a same-identity setup on a second device (the user runs the
   * existing keyring backup/import flow separately for identity).
   * Including the seed would require keyring-layer cooperation; v9.x.
   *
   * What IS in the bundle:
   *   - social_subscriptions (group memberships)
   *   - social_groups (own + mirrored)
   *   - contacts
   *   - peer_book (trust state)
   *   - federation_links
   *   - read_state
   */
  async exportForDevice(opts: {
    passphrase: string;
    includeMessages?: boolean;
  }): Promise<{ bundleBase64: string; sizeBytes: number }> {
    if (opts.passphrase.length < 8) {
      throw new Error('RecoveryManager.exportForDevice: passphrase must be ≥8 chars');
    }
    const db = this.#ctx.storage.db;
    const v9Snapshot = {
      v: 1 as const,
      kind: 'alds-cross-device-bundle' as const,
      createdAt: new Date().toISOString(),
      tables: {
        social_subscriptions: await db.social_subscriptions.toArray(),
        social_groups: await db.social_groups.toArray(),
        contacts: (await db.contacts.toArray()).map(serializeRowFields),
        peer_book: (await db.peer_book.toArray()).map(serializeRowFields),
        federation_links: await db.federation_links.toArray(),
        read_state: await db.read_state.toArray(),
        ...(opts.includeMessages === true
          ? { message_cache: (await db.message_cache.toArray()).map(serializeRowFields) }
          : {}),
      },
    };
    const plaintext = new TextEncoder().encode(JSON.stringify(v9Snapshot));

    // Derive AES-GCM key from passphrase via argon2id (same params as keyring).
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const { argon2id } = await import('hash-wasm');
    const keyBytes = await argon2id({
      password: opts.passphrase,
      salt,
      iterations: 3,
      memorySize: 64 * 1024,
      parallelism: 1,
      hashLength: 32,
      outputType: 'binary',
    });
    const key = await crypto.subtle.importKey('raw', keyBytes as BufferSource, 'AES-GCM', false, ['encrypt']);
    const nonce = crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = new Uint8Array(
      await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce as BufferSource }, key, plaintext as BufferSource),
    );

    // Frame: salt(16) || nonce(12) || ciphertext
    const out = new Uint8Array(16 + 12 + ciphertext.length);
    out.set(salt, 0);
    out.set(nonce, 16);
    out.set(ciphertext, 28);
    return { bundleBase64: bytesToBase64Url(out), sizeBytes: out.length };
  }

  /**
   * Import a previously-exported bundle. Decrypts with the passphrase +
   * writes rows into the local DB. Idempotent on subscriptions / groups /
   * contacts (uses `put`).
   *
   * Returns counts so the UI can show progress.
   */
  async importBundle(opts: {
    bundleBase64: string;
    passphrase: string;
    /** When true (default), existing rows are overwritten. */
    overwrite?: boolean;
  }): Promise<{ groups: number; subscriptions: number; contacts: number; peers: number; links: number; readStates: number }> {
    const overwrite = opts.overwrite ?? true;
    const raw = base64UrlToBytes(opts.bundleBase64);
    if (raw.length < 28 + 16) {
      throw new Error('RecoveryManager.importBundle: bundle too short');
    }
    const salt = raw.slice(0, 16);
    const nonce = raw.slice(16, 28);
    const ciphertext = raw.slice(28);

    const { argon2id } = await import('hash-wasm');
    const keyBytes = await argon2id({
      password: opts.passphrase,
      salt,
      iterations: 3,
      memorySize: 64 * 1024,
      parallelism: 1,
      hashLength: 32,
      outputType: 'binary',
    });
    const key = await crypto.subtle.importKey('raw', keyBytes as BufferSource, 'AES-GCM', false, ['decrypt']);
    let plaintext: Uint8Array;
    try {
      const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: nonce as BufferSource }, key, ciphertext as BufferSource);
      plaintext = new Uint8Array(decrypted);
    } catch {
      throw new Error('RecoveryManager.importBundle: passphrase wrong or bundle tampered');
    }

    const parsed = JSON.parse(new TextDecoder().decode(plaintext)) as {
      v: number; kind: string; tables: Record<string, unknown[]>;
    };
    if (parsed.kind !== 'alds-cross-device-bundle' || parsed.v !== 1) {
      throw new Error('RecoveryManager.importBundle: unknown bundle kind/version');
    }

    const db = this.#ctx.storage.db;
    const counts = { groups: 0, subscriptions: 0, contacts: 0, peers: 0, links: 0, readStates: 0 };

    const importTable = async <T extends { id?: string }>(
      table: { put: (r: T) => Promise<unknown>; get: (id: string) => Promise<T | undefined> },
      rows: T[],
      key: 'id',
      counter: keyof typeof counts,
    ): Promise<void> => {
      for (const r of rows) {
        if (!overwrite && r[key] !== undefined) {
          const existing = await table.get(r[key] as string);
          if (existing !== undefined) continue;
        }
        await table.put(r);
        (counts[counter] as number) += 1;
      }
    };

    const subs = (parsed.tables.social_subscriptions ?? []) as Array<{ id: string }>;
    const groups = (parsed.tables.social_groups ?? []) as Array<{ id: string }>;
    const links = (parsed.tables.federation_links ?? []) as Array<{ id: string }>;
    const reads = (parsed.tables.read_state ?? []) as Array<{ id: string }>;
    await importTable(db.social_groups as unknown as Parameters<typeof importTable>[0], groups, 'id', 'groups');
    await importTable(db.social_subscriptions as unknown as Parameters<typeof importTable>[0], subs, 'id', 'subscriptions');
    await importTable(db.federation_links as unknown as Parameters<typeof importTable>[0], links, 'id', 'links');
    await importTable(db.read_state as unknown as Parameters<typeof importTable>[0], reads, 'id', 'readStates');

    // contacts + peer_book have different PK shapes — handle separately.
    const contacts = (parsed.tables.contacts ?? []) as Array<{ id: string; peer_book_blob?: Uint8Array }>;
    for (const c of contacts) {
      await db.contacts.put(c as never);
      counts.contacts += 1;
    }
    const peers = (parsed.tables.peer_book ?? []) as Array<{ peerId: string }>;
    for (const p of peers) {
      await db.peer_book.put(p as never);
      counts.peers += 1;
    }

    await this.audit({
      category: 'recovery',
      action: 'recovery.bundle-imported',
      context: null,
      details: { ...counts },
    });

    return counts;
  }

  private async collectSnapshotContents(scope: SnapshotScope): Promise<SnapshotContents> {
    const db = this.#ctx.storage.db;
    const tables: SnapshotContents['tables'] = {};

    if (scope === 'keyring-only') {
      // TODO(beta): include keyring blob via keyring API.
      return { v: 1, scope, tables };
    }

    // Envelopes + streams (always included for restore correctness)
    tables.envelopes = (await db.envelopes.toArray()).map(serializeRowFields);
    tables.streams = (await db.streams.toArray()).map(serializeRowFields);

    if (scope === 'keyring+envelopes') {
      return { v: 1, scope, tables };
    }

    // 'full' — also include threads, contacts, vault metadata, governance, recovery state.
    tables.threads_index = (await db.threads_index.toArray()).map(serializeRowFields);
    tables.mls_state = (await db.mls_state.toArray()).map(serializeRowFields);
    tables.message_cache = (await db.message_cache.toArray()).map(serializeRowFields);
    tables.contacts = (await db.contacts.toArray()).map(serializeRowFields);
    tables.own_key_packages = (await db.own_key_packages.toArray()).map(serializeRowFields);
    tables.read_state = (await db.read_state.toArray()).map(serializeRowFields);
    tables.mute_table = (await db.mute_table.toArray()).map(serializeRowFields);
    tables.peer_book = (await db.peer_book.toArray()).map(serializeRowFields);
    tables.invite_ledger = (await db.invite_ledger.toArray()).map(serializeRowFields);
    tables.funnel_definitions = (await db.funnel_definitions.toArray()).map(serializeRowFields);
    tables.funnel_drafts = (await db.funnel_drafts.toArray()).map(serializeRowFields);
    tables.own_submissions = (await db.own_submissions.toArray()).map(serializeRowFields);
    // Note: blobs, audit_log, outbound_queue, inbound_queue, recovery_snapshots
    // are excluded from 'full' snapshots intentionally:
    // - blobs: very large, separate backup story
    // - audit_log: forensic record, should not be tampered with
    // - queues + snapshots: state of the recovery system itself, restoring them is recursive nonsense

    return { v: 1, scope, tables };
  }

  private async audit(input: {
    category: AuditLogRecord['category'];
    action: string;
    context: string | null;
    details: Record<string, unknown>;
  }): Promise<void> {
    const ctx = this.#ctx;
    const occurredAt = new Date().toISOString();
    const canonical = JSON.stringify({
      category: input.category,
      action: input.action,
      context: input.context,
      details: input.details,
      occurredAt,
    });
    const signature = await ctx.identity.sign(
      new TextEncoder().encode(canonical),
      { environment: 'chat', purpose: 'thread-default' },
    );
    const record: AuditLogRecord = {
      seq: undefined as unknown as number,
      category: input.category,
      action: input.action,
      context: input.context,
      details: input.details,
      occurredAt,
      signature,
    };
    try {
      await ctx.storage.db.audit_log.add(record);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`RecoveryManager.audit: ${errMsg(err)}`);
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

interface SnapshotContents {
  v: 1;
  scope: SnapshotScope;
  tables: Record<string, unknown[]>;
}

function serializeEnvelope(env: SignedEnvelope): Uint8Array {
  return new TextEncoder().encode(JSON.stringify(env));
}

function deserializeEnvelope(bytes: Uint8Array): SignedEnvelope {
  const obj = JSON.parse(new TextDecoder().decode(bytes)) as SignedEnvelope;
  if (typeof obj !== 'object' || obj === null || typeof obj.hash !== 'string') {
    throw new Error('deserializeEnvelope: not a SignedEnvelope');
  }
  return obj;
}

/**
 * Convert a row with Uint8Array fields into a JSON-serializable form
 * by base64-encoding the binary fields. Reversed by deserializeRowFields.
 */
function serializeRowFields(row: unknown): unknown {
  if (typeof row !== 'object' || row === null) return row;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(row)) {
    if (v instanceof Uint8Array) {
      out[k] = { __bytes: bytesToBase64(v) };
    } else {
      out[k] = v;
    }
  }
  return out;
}

function deserializeRowFields(row: unknown): unknown {
  if (typeof row !== 'object' || row === null) return row;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(row)) {
    if (
      typeof v === 'object' && v !== null &&
      typeof (v as { __bytes?: unknown }).__bytes === 'string'
    ) {
      out[k] = base64ToBytes((v as { __bytes: string }).__bytes);
    } else {
      out[k] = v;
    }
  }
  return out;
}

function bytesToBase64Url(b: Uint8Array): string {
  const s = bytesToBase64(b);
  return s.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlToBytes(s: string): Uint8Array {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  return base64ToBytes((s + pad).replace(/-/g, '+').replace(/_/g, '/'));
}

function bytesToBase64(b: Uint8Array): string {
  let s = '';
  for (let i = 0; i < b.length; i++) s += String.fromCharCode(b[i]!);
  return btoa(s);
}

function base64ToBytes(b64: string): Uint8Array {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
